import { Injectable } from '@angular/core';
import { environment } from '../environment';

export type MetabaseDashboardKey = 'projectInventory' | 'projectDetail' | 'scenarioInventory' | 'scenarioDetail' | 'runCompare' | 'runPrbu' | 'runPrbuSectorDetail' | 'runPrbuSubscriberDelta' | 'engineeringCompare' | 'executiveCompare' | 'jobTimingDetail' | 'jobTimingHistory';

export interface MetabaseScenarioDetailContext {
  projectId: number;
  scenarioId: number;
  revisionId?: number | null;
}

export interface MetabaseRunCompareContext {
  projectId: number;
  afterRunId: number;
  scenarioId?: number | null;
  revisionId?: number | null;
}

@Injectable({ providedIn: 'root' })
export class MetabaseReportService {
  projectInventoryUrl(): string | null {
    return this.buildDashboardUrl('projectInventory');
  }

  projectDetailUrl(projectId: number): string | null {
    return this.buildDashboardUrl('projectDetail', { project_id: projectId });
  }

  scenarioDetailUrl(context: MetabaseScenarioDetailContext): string | null {
    return this.buildDashboardUrl('scenarioDetail', {
      project_id: context.projectId,
      scenario_id: context.scenarioId,
      revision_id: context.revisionId,
    }) ?? this.projectDetailUrl(context.projectId);
  }

  runCompareUrl(context: MetabaseRunCompareContext): string | null {
    return this.buildDashboardUrl('runCompare', {
      project_id: context.projectId,
      after_run_id: context.afterRunId,
    }) ?? this.buildDashboardUrl('engineeringCompare', {
      project_id: context.projectId,
      after_run_id: context.afterRunId,
    }) ?? (context.scenarioId != null
      ? this.scenarioDetailUrl({
        projectId: context.projectId,
        scenarioId: context.scenarioId,
        revisionId: context.revisionId,
      })
      : this.projectDetailUrl(context.projectId));
  }

  engineeringCompareUrl(context: MetabaseRunCompareContext): string | null {
    return this.buildDashboardUrl('engineeringCompare', {
      project_id: context.projectId,
      after_run_id: context.afterRunId,
    }) ?? this.runCompareUrl(context);
  }

  runPrbuUrl(context: MetabaseRunCompareContext): string | null {
    return this.buildDashboardUrl('runPrbu', {
      project_id: context.projectId,
      after_run_id: context.afterRunId,
    }) ?? this.buildDashboardUrl('runPrbuSectorDetail', {
      project_id: context.projectId,
      after_run_id: context.afterRunId,
    }) ?? this.buildDashboardUrl('runPrbuSubscriberDelta', {
      project_id: context.projectId,
      after_run_id: context.afterRunId,
    }) ?? this.runCompareUrl(context);
  }

  runPrbuSectorDetailUrl(context: MetabaseRunCompareContext): string | null {
    return this.buildDashboardUrl('runPrbuSectorDetail', {
      project_id: context.projectId,
      after_run_id: context.afterRunId,
    }) ?? this.runPrbuUrl(context);
  }

  runPrbuSubscriberDeltaUrl(context: MetabaseRunCompareContext): string | null {
    return this.buildDashboardUrl('runPrbuSubscriberDelta', {
      project_id: context.projectId,
      after_run_id: context.afterRunId,
    }) ?? this.runPrbuUrl(context);
  }

  jobTimingDetailUrl(jobId: string | null | undefined): string | null {
    const normalizedJobId = String(jobId ?? '').trim();
    if (!normalizedJobId) {
      return null;
    }
    return this.buildDashboardUrl('jobTimingDetail', { job_id: normalizedJobId });
  }

  jobTimingHistoryUrl(): string | null {
    return this.buildDashboardUrl('jobTimingHistory');
  }

  private buildDashboardUrl(
    key: MetabaseDashboardKey,
    params?: Record<string, string | number | null | undefined>,
  ): string | null {
    const baseUrl = environment.metabaseBaseUrl?.trim();
    const dashboards = environment.metabaseDashboards as Partial<Record<MetabaseDashboardKey, string>> | undefined;
    const dashboardPath = dashboards?.[key]?.trim();
    if (!baseUrl || !dashboardPath) {
      return null;
    }

    const resolvedBaseUrl = this.resolveBaseUrl(baseUrl);
    if (!resolvedBaseUrl) {
      return null;
    }

    let url: URL;
    try {
      url = new URL(dashboardPath, resolvedBaseUrl);
    } catch {
      return null;
    }

    for (const [param, value] of Object.entries(params ?? {})) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      url.searchParams.set(param, String(value));
    }
    return url.toString();
  }

  private resolveBaseUrl(baseUrl: string): string | null {
    try {
      return new URL(baseUrl).toString();
    } catch {
      const browserBase = globalThis.location?.origin ?? globalThis.location?.href;
      if (!browserBase) {
        return null;
      }

      try {
        return new URL(baseUrl, browserBase).toString();
      } catch {
        return null;
      }
    }
  }
}